import { Button } from '@/components/ui/button';
import { fetchUserAttributes, updatePassword } from 'aws-amplify/auth';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Input } from '@/components/form/Input';
import { Inputs } from '@/components/form/Inputs';
import { Modal } from '@/components/Modal';
import { Container } from '@/components/legacy-ui';
import {
  isPasswordNotValid,
  PasswordRequirement,
} from '@/components/legacy-ui/PasswordRequirement';
import { useUpdatePasswordResetTimestamp } from '@/hooks/useUpdateUserAttributes';
import { SignupError } from '@/sections/signup/SignupError';
import { formatDate } from '@/utils/date.util';

export function PasswordReset() {
  const [isPasswordResetModalOpen, setIsPasswordResetModalOpen] =
    useState(false);
  const [lastPasswordChange, setLastPasswordChange] = useState<string>('');
  const [refetchCounter, setRefetchCounter] = useState(0);

  const refetchAttributes = useCallback(() => {
    setRefetchCounter(prev => prev + 1);
  }, []);

  useEffect(() => {
    async function fetchLastPasswordChange() {
      try {
        const attributes = await fetchUserAttributes();

        // Look for custom attribute that stores last password change
        if (attributes?.updated_at) {
          const timestamp = attributes.updated_at;
          const isoString = new Date(parseInt(timestamp)).toISOString();
          const timeAgo = formatDate(isoString, undefined, true);
          setLastPasswordChange(`You last changed your password ${timeAgo}`);
        } else {
          // No password change timestamp found
          setLastPasswordChange('No password change history available');
        }
      } catch (error) {
        console.error('Error fetching user attributes:', error);
        setLastPasswordChange('Unable to retrieve password history');
      }
    }

    fetchLastPasswordChange();
  }, [refetchCounter]);

  return (
    <Container className="flex items-center justify-between">
      <div>
        <h3 className="text-lg font-bold">Password Reset</h3>
        <p>{lastPasswordChange}</p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="h-fit"
        onClick={() => {
          setIsPasswordResetModalOpen(true);
        }}
      >Change Your Password</Button>
      <PasswordResetModal
        open={isPasswordResetModalOpen}
        onClose={() => setIsPasswordResetModalOpen(false)}
        onPasswordChange={refetchAttributes}
      />
    </Container>
  );
}

export const PasswordResetModal = ({
  open,
  onClose,
  onPasswordChange,
}: {
  open: boolean;
  onClose: () => void;
  onPasswordChange: () => void;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [form, setForm] = useState<{
    oldPassword: string;
    newPassword: string;
    newPasswordAgain: string;
  }>({
    oldPassword: '',
    newPassword: '',
    newPasswordAgain: '',
  });
  const updatePasswordResetTimestamp = useUpdatePasswordResetTimestamp();

  async function handleConfirmResetPassword() {
    try {
      setIsLoading(true);

      // Update password
      await updatePassword({
        oldPassword: form.oldPassword,
        newPassword: form.newPassword,
      });

      // After successful password change, store the timestamp
      await updatePasswordResetTimestamp();

      // Trigger refetch of user attributes
      onPasswordChange();
      // Reset form and close modal
      setForm({
        oldPassword: '',
        newPassword: '',
        newPasswordAgain: '',
      });
      onClose();
      toast.success('Password updated successfully');
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('An error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Modal
      hideFooter={true}
      title="Change Your Password"
      open={open}
      onClose={onClose}
    >
      <div className="mb-4">
        <p className="mb-4">
          If you&apos;re working with others, set up collaborator access instead
          of sharing your password.
        </p>

        <form
          id="reset-password"
          className="flex flex-1 flex-col gap-4 space-y-4"
        >
          <Inputs
            inputs={[
              {
                label: 'Old Password',
                value: form.oldPassword,
                placeholder: '**************',
                name: 'oldPassword',
                type: Input.Type.PASSWORD,
                required: true,
              },
              {
                label: 'New Password',
                value: form.newPassword,
                name: 'newPassword',
                type: Input.Type.PASSWORD,
                required: true,
              },
              {
                label: 'Enter New Password Again',
                value: form.newPasswordAgain,
                name: 'newPasswordAgain',
                type: Input.Type.PASSWORD,
                required: true,
              },
            ]}
            onChange={values => {
              setForm(credentials => ({
                ...credentials,
                ...values,
              }));
            }}
          />
          <PasswordRequirement password={form.newPassword} />
          <SignupError error={error} />
          <Button
            disabled={
              isLoading ||
              isPasswordNotValid({ password: form.newPassword }) ||
              form.newPassword !== form.newPasswordAgain
            }
            size="sm"
            className="w-full"
            type="submit"
            id="forgot-password"
            onClick={handleConfirmResetPassword}
          >Change Password</Button>
        </form>
      </div>
    </Modal>
  );
};
